// Announcement.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../App";
import axios from "axios";
import "../../styles/Announcement.css";

function Announcement() {
  const { userType } = useContext(UserContext);
  const navigate      = useNavigate();
  const location      = useLocation();
  const searchParams  = new URLSearchParams(location.search);
  const editId        = searchParams.get("announcementId");

  const [subject, setSubject]     = useState("");
  const [announcement, setAnn]    = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage]     = useState({ text: "", type: "" });

  // Redirect non‑company users
  useEffect(() => {
    if (userType !== "company") navigate("/");
  }, [userType, navigate]);

  // Default dates only for creation
  useEffect(() => {
    if (editId) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const isoToday = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    setStartDate(isoToday);
    const future = new Date(today);
    future.setDate(future.getDate() + 90);
    const isoFuture = `${future.getFullYear()}-${pad(future.getMonth() + 1)}-${pad(future.getDate())}`;
    setEndDate(isoFuture);
  }, [editId]);

  // Fetch existing announcement for editing
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await axios.get(`/announcement/${editId}`, { withCredentials: true });
        if (data.success) {
          const ann = data.announcement;
          setSubject(ann.subject);
          setAnn(ann.message);
          setStartDate(ann.startDate);
          setEndDate(ann.expireDate);
        } else {
          setMessage({ text: data.message || "Failed to load announcement", type: "error" });
        }
      } catch {
        setMessage({ text: "Error loading announcement", type: "error" });
      }
    })();
  }, [editId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      setMessage({ text: "Subject cannot be empty", type: "error" });
      return;
    }
    if (subject.length < 5 || subject.length > 100) {
      setMessage({ text: "Subject must be 5-100 characters", type: "error" });
      return;
    }
    if (!announcement.trim()) {
      setMessage({ text: "Announcement cannot be empty", type: "error" });
      return;
    }
    if (announcement.length > 500) {
      setMessage({ text: "Announcement ≤ 1000 characters", type: "error" });
      return;
    }
    if (!startDate || !endDate) {
      setMessage({ text: "Start and end dates are required", type: "error" });
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().substring(0, 10);
    if (startDate < todayStr) {
      setMessage({ text: "Start date must be today or later", type: "error" });
      return;
    }
    if (endDate <= startDate) {
      setMessage({ text: "End date must be after start date", type: "error" });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editId) {
        // EDIT flow
        const { data } = await axios.put(
          `/announcement/${editId}`,
          { subject, message: announcement, startDate, endDate },
          { withCredentials: true }
        );
        if (!data.success) {
          setMessage({ text: data.message || "Failed to update", type: "error" });
        } else {
          setMessage({ text: "Announcement updated!", type: "success" });
          setTimeout(() => navigate("/companyannouncements"), 1000);
        }
      } else {
        // CREATE flow
        const { data } = await axios.post(
          "/announcement",
          { subject, announcement, startDate, endDate },
          { withCredentials: true }
        );
        if (!data.success) {
          setMessage({ text: data.message || "Failed to create", type: "error" });
        } else {
          setMessage({ text: "Submitted for review!", type: "success" });
          setSubject("");
          setAnn("");
          // reset dates
          const today2 = new Date(); today2.setHours(0, 0, 0, 0);
          const pad = (n) => String(n).padStart(2, "0");
          const isoToday2 = `${today2.getFullYear()}-${pad(today2.getMonth() + 1)}-${pad(today2.getDate())}`;
          setStartDate(isoToday2);
          const future = new Date(today2); future.setDate(future.getDate() + 90);
          const isoFuture2 = `${future.getFullYear()}-${pad(future.getMonth() + 1)}-${pad(future.getDate())}`;
          setEndDate(isoFuture2);
        }
      }
    } catch {
      setMessage({ text: "An error occurred", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => setMessage({ text: "", type: "" });

  return (
    <div className="Homepage flex flex-col justify-center h-full">
      <div className="flex-grow w-full max-w-6xl mx-auto py-8 px-4">
        <div className="bg-white rounded-2xl shadow p-6 text-black mb-4 max-h-[85vh] overflow-y-auto !max-w-9xl mx-auto">
          <h1 className="text-2xl font-bold mb-4 text-center">
            {editId ? "Edit Announcement" : "Announcement to Students"}
          </h1>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-1 font-semibold">Subject:</label>
              <input
                type="text"
                className="w-full border rounded p-2"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={100}
                required
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold">Announcement Message:</label>
              <textarea
                className="w-full border rounded p-2"
                rows={6}
                maxLength={1000}
                value={announcement}
                onChange={(e) => setAnn(e.target.value)}
                required
              />
            <p className="helperText">
              {"You can use curly brackets {} to indicate Links/URLs e.g. {https://jobhub.cs.vt.edu} becomes "}<a href="https://jobhub.cs.vt.edu" target="_blank" rel="noopener noreferrer">https://jobhub.cs.vt.edu</a>.<br/>
              {"Additionally you can have the link appear as other text by proceeding it with your text and a bar symbol e.g. {link|https://jobhub.cs.vt.edu} becomes "}<a href="https://jobhub.cs.vt.edu" target="_blank" rel="noopener noreferrer">link</a>.<br/>
              {"Of note, all links will open in a new tab and be sure to include the 'https://' or 'http://' at the start of your links or they will not work."}
            </p>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block mb-1 font-semibold">Start Date:</label>
                <input
                  type="date"
                  className="w-full border rounded p-2"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="flex-1">
                <label className="block mb-1 font-semibold">End Date:</label>
                <input
                  type="date"
                  className="w-full border rounded p-2"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex justify-center gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-gray-300 text-black rounded px-6 py-2"
              >
                {isSubmitting
                  ? editId
                    ? "Saving..."
                    : "Sending..."
                  : editId
                  ? "Save Changes"
                  : "Submit Announcement"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/")}
                disabled={isSubmitting}
                className="bg-gray-300 text-black rounded px-6 py-2"
              >
                Return to Home
              </button>
              <button
                type="button"
                onClick={() => navigate("/companyannouncements")}
                disabled={isSubmitting}
                className="bg-gray-300 text-black rounded px-6 py-2"
              >
                View Previous Announcements
              </button>
            </div>
          </form>
        </div>
      </div>
      {message.text && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full text-center">
            <p className={`text-xl font-semibold mb-2 ${message.type === "error" ? "text-red-600" : "text-green-600"}`}>
              {message.type === "error" ? "Error" : "Success"}
            </p>
            <p className="text-gray-700 mb-4">{message.text}</p>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Announcement;
